import { type Dispatch, type SetStateAction, useState } from "react";
import { searchProbeV1BrandsSearchProbePost } from "../api/generated/qcsControlPlane";
import { Flow, type SearchProbeResult } from "../api/generated/model";
import { isPlatform, platformKeysFor } from "../api/platforms";
import { type Platform, pdpUrl, platformLabel } from "../api/labels";
import { Badge, Button, EmptyState, Input, Panel, Row, Stack } from "@akaike/ui";
import { PlayIcon } from "./foundation";

// A draft search candidate being composed in the UI (pre-persistence).
export interface CandidateDraft {
  key: string;
  platform: Platform;
  search_term: string;
  match_aliases: string[];
  testing?: boolean;
  result?: SearchProbeResult | null;
}

let _seq = 0;
export function newDraft(platform: Platform, term = ""): CandidateDraft {
  _seq += 1;
  return { key: `d${_seq}`, platform, search_term: term, match_aliases: [] };
}

// One-default-candidate-per-platform, search_term = the canonical name.
export function defaultDrafts(name: string): CandidateDraft[] {
  return platformKeysFor(Flow.brand_sweep).map((p) => newDraft(p, name));
}

// The non-empty candidates of a draft list (empty rows are ignored on save).
export function realDrafts(drafts: CandidateDraft[]): CandidateDraft[] {
  return drafts.filter((d) => d.search_term.trim().length > 0);
}

// A draft is verified only when its last probe (against the current query) found a hit.
export function draftVerified(d: CandidateDraft): boolean {
  return d.result?.found === true;
}

// The registration gate: >=1 real candidate and every one verified.
export function allDraftsVerified(drafts: CandidateDraft[]): boolean {
  const real = realDrafts(drafts);
  return real.length > 0 && real.every(draftVerified);
}

/**
 * A reusable editor for a list of per-platform search candidates, each with a live
 * "Test" button that probes the real platform search through the proxy pool and lets
 * the operator whitelist the displayed brand strings as match aliases.
 */
export function CandidateEditor({
  drafts,
  onChange,
  defaultTerm,
}: {
  drafts: CandidateDraft[];
  // A React state setter so concurrent probes (Verify all) patch by functional update
  // and never clobber each other's results.
  onChange: Dispatch<SetStateAction<CandidateDraft[]>>;
  defaultTerm?: string;
}) {
  const [verifyingAll, setVerifyingAll] = useState(false);
  const availablePlatforms = platformKeysFor(Flow.brand_sweep);

  const patch = (key: string, p: Partial<CandidateDraft>) =>
    onChange((prev) =>
      prev.map((d) => {
        if (d.key !== key) return d;
        const next = { ...d, ...p };
        // Editing the actual query (term/platform) invalidates a prior probe result,
        // so a candidate can't stay "verified" against a term that was never tested.
        const queryChanged =
          (p.search_term !== undefined && p.search_term !== d.search_term) ||
          (p.platform !== undefined && p.platform !== d.platform);
        if (queryChanged && !("result" in p)) next.result = null;
        return next;
      }),
    );
  const remove = (key: string) => onChange((prev) => prev.filter((d) => d.key !== key));

  const test = async (d: CandidateDraft, termOverride?: string) => {
    const term = (termOverride ?? d.search_term).trim();
    if (!term) return;
    patch(d.key, { testing: true, result: null });
    // A probe walk takes seconds, so the query can move on under it (the operator edits
    // the term, swaps the platform, or clicks another suggestion). Land the result only
    // when the draft is still on the exact query we probed -- otherwise the draft would
    // read "verified" against a term that was never tested, which is precisely what the
    // `patch` invalidation above exists to prevent.
    const land = (result: SearchProbeResult) =>
      onChange((prev) =>
        prev.map((x) => {
          if (x.key !== d.key) return x;
          const stale = x.search_term.trim() !== term || x.platform !== d.platform;
          return stale ? { ...x, testing: false } : { ...x, testing: false, result };
        }),
      );
    try {
      const res = await searchProbeV1BrandsSearchProbePost({
        platform: d.platform,
        search_term: term,
        match_aliases: d.match_aliases,
      });
      land(res);
    } catch (e) {
      land({
        ok: false,
        found: false,
        hit_city: null,
        hit_store_id: null,
        attempts: 0,
        hit_count: 0,
        products: [],
        distinct_brands: [],
        other_brands: [],
        http_status: null,
        error: e instanceof Error ? e.message : "probe failed",
      });
    }
  };

  const addAlias = (d: CandidateDraft, alias: string) => {
    if (d.match_aliases.some((a) => a.toLowerCase() === alias.toLowerCase())) return;
    patch(d.key, { match_aliases: [...d.match_aliases, alias] });
  };

  // Probe every not-yet-verified candidate fully in parallel: each probe is an
  // independent request with its own proxy lease, and `test` patches by functional
  // update keyed on the draft, so concurrent results never clobber each other.
  const pending = drafts.filter((d) => d.search_term.trim() && d.result?.found !== true);
  const verifyAll = async () => {
    if (pending.length === 0) return;
    setVerifyingAll(true);
    await Promise.all(pending.map((d) => test(d)));
    setVerifyingAll(false);
  };

  return (
    <Stack gap="md">
      {availablePlatforms.length === 0 && drafts.length === 0 && (
        <EmptyState compact title="No brand-sweep platforms available">
          Enable a platform with the brand-sweep capability before adding search candidates.
        </EmptyState>
      )}
      {drafts.map((d) => (
        <Panel
          key={d.key}
          style={{
            padding: 10,
            background: "var(--bg-2)",
            border: "1px solid var(--line)",
          }}
        >
          <Row gap="sm" wrap>
            <select
              className="input"
              style={{ width: 110 }}
              value={d.platform}
              onChange={(e) => {
                const picked = e.currentTarget.value;
                if (isPlatform(picked)) patch(d.key, { platform: picked });
              }}
            >
              {availablePlatforms.includes(d.platform) ? null : (
                <option value={d.platform}>{platformLabel(d.platform)} (unavailable)</option>
              )}
              {availablePlatforms.map((p) => (
                <option key={p} value={p}>
                  {platformLabel(p)}
                </option>
              ))}
            </select>
            <Input
              style={{ flex: 1, minWidth: 150 }}
              placeholder="search term (typed into platform search)"
              value={d.search_term}
              onChange={(e) => patch(d.key, { search_term: e.currentTarget.value })}
            />
            <Button
              variant="primary"
              size="sm"
              loading={d.testing}
              disabled={!d.search_term.trim()}
              onClick={() => test(d)}
            >
              {d.testing ? "Testing…" : <>Test <PlayIcon /></>}
            </Button>
            <Button variant="outline" size="sm" icon onClick={() => remove(d.key)} aria-label="remove candidate">
              ✕
            </Button>
          </Row>

          {d.match_aliases.length > 0 && (
            <Row gap="sm" wrap style={{ marginTop: 8 }}>
              <span className="faint" style={{ fontSize: 11 }}>
                aliases:
              </span>
              {d.match_aliases.map((a) => (
                <Badge key={a} variant="outline-primary" style={{ fontSize: 11 }}>
                  {a}
                  <button
                    style={{
                      marginLeft: 4,
                      background: "none",
                      border: 0,
                      color: "inherit",
                      cursor: "pointer",
                    }}
                    onClick={() =>
                      patch(d.key, {
                        match_aliases: d.match_aliases.filter((x) => x !== a),
                      })
                    }
                  >
                    ✕
                  </button>
                </Badge>
              ))}
            </Row>
          )}

          {d.result && (
            <ProbeReport
              result={d.result}
              platform={d.platform}
              onAddAlias={(a) => addAlias(d, a)}
              onUseTerm={(t) => {
                patch(d.key, { search_term: t });
                void test(d, t);
              }}
            />
          )}
        </Panel>
      ))}

      <Row gap="sm">
        <Button
          variant="outline"
          size="sm"
          disabled={availablePlatforms.length === 0}
          onClick={() =>
            onChange((prev) => [
              ...prev,
              newDraft(availablePlatforms[0], defaultTerm ?? ""),
            ])
          }
        >
          + Add candidate
        </Button>
        {defaultTerm && drafts.length === 0 && (
          <Button variant="outline" size="sm" onClick={() => onChange(defaultDrafts(defaultTerm))}>
            One per platform from name
          </Button>
        )}
        <div className="flex-1" />
        {drafts.some((d) => d.search_term.trim()) && (
          <Button
            variant="primary"
            size="sm"
            loading={verifyingAll}
            disabled={pending.length === 0}
            onClick={verifyAll}
          >
            {verifyingAll
              ? "Verifying all…"
              : pending.length === 0
                ? "✓ All verified"
                : `Verify all (${pending.length})`}
          </Button>
        )}
      </Row>
    </Stack>
  );
}

export function ProbeReport({
  result,
  platform,
  onAddAlias,
  onUseTerm,
}: {
  result: SearchProbeResult;
  platform: Platform;
  onAddAlias: (alias: string) => void;
  // Retry the probe with a different search term. Offered on a miss where the
  // storefront's own brand label differs from what was typed ("24 Mantra Organic"
  // -> "24 Mantra"): whitelisting an alias keeps searching the dead term, so the
  // operator usually wants the candidate's term replaced instead.
  onUseTerm?: (term: string) => void;
}) {
  const products = (result.products ?? []) as Array<{
    product_id: string;
    name?: string | null;
    image_url?: string | null;
    brand?: string | null;
  }>;
  const distinctBrands = result.distinct_brands ?? [];
  const otherBrands = result.other_brands ?? [];
  return (
    <div style={{ marginTop: 8, fontSize: 12 }}>
      {result.found ? (
        <div style={{ color: "var(--ok)" }}>
          ✓ found {result.hit_count} at <strong>{result.hit_city}</strong> ({result.attempts}{" "}
          {result.attempts === 1 ? "store" : "stores"} probed)
        </div>
      ) : (
        <div style={{ color: result.error ? "var(--crit)" : "var(--warn)" }}>
          {result.error
            ? `✗ ${result.error}`
            : `✗ not found (${result.attempts} ${result.attempts === 1 ? "store" : "stores"} probed)`}
        </div>
      )}

      {products.length > 0 && (
        <Row gap="sm" wrap style={{ marginTop: 6 }}>
          {products.slice(0, 6).map((p) => (
            <a
              key={p.product_id}
              href={pdpUrl(platform, p.product_id)}
              target="_blank"
              rel="noopener noreferrer"
              title={`Open "${p.name ?? p.product_id}" on the storefront`}
              style={{
                width: 64,
                textAlign: "center",
                color: "inherit",
                textDecoration: "none",
              }}
            >
              {p.image_url && (
                <img
                  src={p.image_url}
                  alt={p.name ?? ""}
                  style={{
                    width: 56,
                    height: 56,
                    objectFit: "contain",
                    borderRadius: 6,
                  }}
                />
              )}
              <div className="faint" style={{ fontSize: 9, lineHeight: 1.2, marginTop: 2 }}>
                {p.brand} ↗
              </div>
            </a>
          ))}
        </Row>
      )}

      {distinctBrands.length > 0 && (
        <Row gap="sm" wrap style={{ marginTop: 8 }}>
          <span className="faint" style={{ fontSize: 11 }}>
            matching brands (click to whitelist):
          </span>
          {distinctBrands.map((b) => (
            <Badge asChild key={b} variant="outline-primary">
              <button
                style={{ fontSize: 11, cursor: "pointer" }}
                onClick={() => onAddAlias(b)}
              >
                + {b}
              </button>
            </Badge>
          ))}
        </Row>
      )}

      {!result.found && onUseTerm && distinctBrands.length > 0 && (
        <Row gap="sm" wrap style={{ marginTop: 6 }}>
          <span className="faint" style={{ fontSize: 11 }}>
            or search that term instead:
          </span>
          {distinctBrands.map((b) => (
            <Badge asChild key={b} variant="outline-success">
              <button
                style={{ fontSize: 11, cursor: "pointer" }}
                title={`Replace this candidate's search term with "${b}" and re-test`}
                onClick={() => onUseTerm(b)}
              >
                {b} <PlayIcon />
              </button>
            </Badge>
          ))}
        </Row>
      )}

      {otherBrands.length > 0 && (
        <Row gap="sm" wrap style={{ marginTop: 6 }}>
          <span className="faint" style={{ fontSize: 10.5 }}>
            other brands in results (likely sponsored):
          </span>
          {otherBrands.map((b) => (
            <Badge asChild key={b} variant="outline">
              <button
                style={{ fontSize: 10.5, cursor: "pointer", opacity: 0.6 }}
                title="A different brand appeared in results: whitelist only if it's genuinely this brand"
                onClick={() => onAddAlias(b)}
              >
                + {b}
              </button>
            </Badge>
          ))}
        </Row>
      )}
    </div>
  );
}
